import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa6';
import { CheckIcon } from '../assets/icons';

/**
 *
 * @param {{
 *   type: 'text' | 'select' | 'radio' | 'checkbox' | 'textarea',
 *   htmlType?: 'text' | 'email' | 'password' | 'number',
 *   label?: string,
 *   paragraphLabel?: string,
 *   placeholder?: string,
 *   defaultValue?: string,
 *   options?: {
 *     value: string,
 *     label: string,
 *     disabled?: boolean,
 *   }[],
 *   onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
 *   setSearch?: (search: string) => void,
 *   inputClassName?: string,
 *   labelClassName?: string,
 *   paragraphLabelClassName?: string,
 *   errorClassName?: string,
 *   radioBGColor?: string,
 *   radioClassName?: string,
 *   loading?: boolean,
 *   error?: string,
 * }} props
 * @returns {JSX.Element}
 */

const Input = ({
  type,
  htmlType = 'text',
  label,
  paragraphLabel,
  name,
  id,
  placeholder,
  defaultValue = '',
  options,
  onChange = () => {},
  inputClassName,
  labelClassName,
  paragraphLabelClassName,
  errorClassName,
  radioBGColor = 'white',
  radioClassName,
  error,
  setSearch,
  loading,
  disabled,
  showCheckboxOnLeft = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative">
      {label && (
        <label
          dangerouslySetInnerHTML={{ __html: label }}
          htmlFor={id}
          className={`font-inter text-sm font-semibold text-gray-10 mb-3 block text-left ${labelClassName}`}
        />
      )}
      {paragraphLabel && (
        <p
          dangerouslySetInnerHTML={{ __html: paragraphLabel }}
          className={`font-inter text-sm font-semibold text-gray-10 mb-3 block text-left ${paragraphLabelClassName}`}
        />
      )}
      <InputType
        type={type}
        htmlType={htmlType}
        name={name}
        id={id}
        placeholder={placeholder}
        options={options}
        onChange={onChange}
        value={defaultValue}
        className={inputClassName}
        setSearch={setSearch}
        radioBGColor={radioBGColor}
        radioClassName={radioClassName}
        disabled={disabled}
        showCheckboxOnLeft={showCheckboxOnLeft}
      />

      {error && (
        <div
          className={`error-field text-red-20 text-xs mt-1 ${errorClassName}`}
          dangerouslySetInnerHTML={{ __html: t(`errors.${error}`) }}
        />
      )}

      {loading && (
        <div className="absolute top-0 right-0">
          <FaSpinner size={20} color="#313131" className="animate-spin" />
        </div>
      )}

      {/* Hidden span for tailwind colors initialization */}
      <span className="hidden bg-gray-30" />
    </div>
  );
};

const defaultClasses =
  'font-inter w-full rounded-xl px-3 py-3 text-sm text-black placeholder:text-gray-10/60 bg-white outline-none border-none disabled:opacity-50 disabled:cursor-not-allowed';

const InputType = ({
  type,
  htmlType = 'text',
  name,
  id,
  placeholder,
  options,
  onChange,
  value,
  className,
  setSearch,
  radioBGColor,
  radioClassName,
  disabled,
  showCheckboxOnLeft = false,
}) => {
  const [selectedSearchOption, setSelectedSearchOption] = useState('');
  const [localSearch, setLocalSearch] = useState({
    query: '',
    result: '',
  });

  useEffect(() => {
    setLocalSearch({
      query: value,
      result: value,
    });
    setSelectedSearchOption(value);
  }, [value]);

  if (type === 'select') {
    return (
      <>
        {!setSearch && (
          <select
            name={name}
            id={id}
            placeholder={placeholder}
            value={value || ''}
            onChange={onChange}
            className={`${defaultClasses} ${className} ${value ? 'text-gray-10' : 'text-gray-10/60'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={disabled}>
            <option value={''}>{placeholder}</option>
            {options.map((option, index) => (
              <option
                key={option.value + index}
                value={option.value}
                disabled={option.disabled}
                className={`${option.disabled ? 'text-gray-10/60' : 'text-gray-10'}`}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        {setSearch && (
          <div className="relative">
            <input
              type="text"
              name={name}
              id={id}
              placeholder={placeholder}
              value={localSearch.query}
              onChange={(e) => {
                setSelectedSearchOption('');
                setLocalSearch({ ...localSearch, query: e.target.value });
                setSearch({ ...setSearch, query: e.target.value });
              }}
              className={`${defaultClasses} ${className}`}
              disabled={disabled}
            />

            {options?.length > 0 && !selectedSearchOption && (
              <div className="absolute top-[110%] left-0 shadow-lg w-full h-[200px] overflow-y-auto p-4 bg-white rounded-xl z-10">
                {options.map((option) => (
                  <div
                    key={option.value}
                    className="cursor-pointer p-2 border-b border-gray-10/10"
                    onClick={() => {
                      setSelectedSearchOption(option.value);
                      setLocalSearch({ query: option.value, result: option.value });
                      onChange({
                        target: {
                          name: name,
                          value: option.value,
                        },
                      });
                    }}>
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  if (type === 'radio') {
    return (
      <div className={`flex flex-row items-center gap-4 ${radioClassName}`}>
        {options.map((option) => (
          <div key={option.value} className="relative">
            <label
              htmlFor={id + '-' + option.value}
              className={`cursor-pointer text-xs text-gray-10 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed ${showCheckboxOnLeft ? 'flex-row-reverse gap-4 w-full justify-between' : 'flex-col'}`}>
              <input
                type="radio"
                name={name}
                id={id + '-' + option.value}
                placeholder={placeholder}
                checked={option.value === value}
                onChange={() => {
                  onChange(option.value);
                }}
                className="absolute top-0 left-0 opacity-0 w-0 h-0"
                disabled={disabled}
              />
              <span
                dangerouslySetInnerHTML={{ __html: option.label }}
                className={`text-sm ${showCheckboxOnLeft && 'w-11/12 sm:w-[95%]'}`}
              />
              <span
                className={`bg-${option.value === value ? 'navy-10' : radioBGColor} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} rounded-lg w-8 h-8 p-2 flex items-center justify-center`}>
                {option.value === value && <CheckIcon />}
              </span>
            </label>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'checkbox') {
    return (
      <div className="flex flex-row items-center gap-2">
        {options.map((option) => (
          <div className="flex flex-col items-center justify-center gap-2">
            <label dangerouslySetInnerHTML={{ __html: option.label }} htmlFor={option.value} />
            <input
              type="checkbox"
              name={name}
              id={id}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              disabled={disabled}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        ))}
      </div>
    );
  }

  if (type === 'textarea') {
    return (
      <textarea
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`${defaultClasses} ${className} min-h-[100px]`}
        disabled={disabled}
      />
    );
  }

  return (
    <input
      id={id}
      name={name}
      type={htmlType}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`${defaultClasses} ${className}`}
      disabled={disabled}
    />
  );
};

export default Input;
